'use client'

import { COLLECTION_RAIL_WIDTH } from '@/utils/collection-layout'

import React, { FC, ReactElement, useEffect, useMemo, useState, useRef, useCallback } from 'react'
import useWindowSize from '@/utils/hooks/useWindowSize'
import useComponentSize from '@/utils/hooks/useComponentSize'
import isFungible from '@/utils/is-fungible'
import { DESKTOP_COLLECTION_CHART_CLASS, MOBILE_COLLECTION_CHART_CLASS } from '../chart-frame'
import CollectionChart from '../tv-collection-chart'
import CollectionFeed from '../collection-feed'
import RemoteCollectionFeed from '../remote-collection-feed'
import event from '@/utils/event'
import ErrorBoundary from '@/components/error-boundary'
import useCollectionAttributes from '@/utils/hooks/useCollectionAttributes'
import { getTapTickerFromSlug } from '@/utils/tap-token'

import { CollectionStats } from '../v2/production-token-stats'
import ActivitySocialPanel, { type SidebarTab } from '@/components/collection-social/sidebar-panel'
import { Feed } from '../v2/feed'
import NatOffersPanel from '@/components/offers/nat-offers-panel'
import { NatOwners } from '../v2/nat-owners'
import { BarChart2, History, ShoppingCart, ChevronLeft, Info } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import CollectionIcon from '@/components/collection-icon'
import VerifiedBadge from '@/components/verified-badge'
import { isCollectionObjectSlug } from '@/utils/collection-object'

const CollectionComponent: FC<{
	metaData: any
	collectionStats: any
	collection_slug: any
	isRemote: boolean
	title: any
	inscriptions: any[]
	isMobile: boolean
	leftPanel?: React.ReactNode
	headerActions?: React.ReactNode
}> = (props): ReactElement => {
	// The production desktop rails need at least 1024px to leave room for the chart.
	const { width: viewportWidth } = useWindowSize()
	const isMobile = viewportWidth < 1024
	const { width, ref } = useComponentSize()
	const tapTicker = getTapTickerFromSlug(props.collection_slug)
	const hasTapAlpha = !!tapTicker
	const isTapDmtNatSlug = tapTicker === 'dmt-nat'
	// NFT collections almost always render a flat or near-flat chart on the
	// mobile chart-first landing because floor price moves rarely. Skip the
	// chart tab entirely for non-fungibles on mobile so users land directly on
	// the trade view. Fungibles (TAP / runes / BRC-20) keep chart-first.
	const isFungibleSlug = Boolean(isFungible(props.collection_slug) || tapTicker)
	const initialMobileTab = isFungibleSlug ? 'chart' : 'feed'
	const [activeTab, setActiveTab] = useState(isMobile ? initialMobileTab : 'feed')
	const [_selectionCount, setSelectionCount] = useState(0)

	// Tab switches on mobile preserve page scroll, which makes "Trade now" /
	// back buttons feel broken when the user has scrolled the chart tab. Reset
	// scroll on every tab switch so the new tab opens at the top. The rAF
	// defers the scroll past the radix TabsContent render that would otherwise
	// re-snap scroll position back to where it was.
	const switchTab = useCallback((tab: string) => {
		setActiveTab(tab)
		if (typeof window !== 'undefined') {
			window.scrollTo({ top: 0 })
			requestAnimationFrame(() => window.scrollTo({ top: 0 }))
		}
	}, [])

	// Snap to top on initial mount. Without this, browser scroll restoration
	// (or a stale Next.js scroll position from the previous route) can land
	// the user at the bottom of the now-shorter chart-first mobile layout.
	useEffect(() => {
		if (typeof window === 'undefined') return
		window.scrollTo({ top: 0 })
		requestAnimationFrame(() => window.scrollTo({ top: 0 }))
	}, [])

	// Mirrors the chart's currently-selected range so the header's change
	// metric flips from "7D" to "1D"/"1M"/etc. in sync with the chart tabs.
	const [chartRange, setChartRange] = useState<{ label: string; value: number } | null>(null)
	const handleChartRangeChange = useCallback((label: string, value: number) => {
		setChartRange((prev) =>
			prev?.label === label && Math.abs(prev.value - value) < 0.000001 ? prev : { label, value }
		)
	}, [])

	const { attributes } = useCollectionAttributes(props.collection_slug)

	const handleSelectBulk = React.useCallback((inscription: any) => {
		if (!inscription.escrow) {
			return
		}

		event.emit('add-to-cart', {
			id: inscription.id
		})
	}, [])

	const rootRef = useRef<HTMLDivElement>(null)
	const headerRef = useRef<HTMLDivElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	// CSS owns the desktop pane from the first paint. A post-mount measure
	// used to start the chart at 100vh and then shrink it.
	const containerHeight = '100%'

	const isFungibleCollection = isFungibleSlug
	const isRune = Boolean(props.collection_slug?.startsWith('rune-'))
	const isTapDmtNat = isTapDmtNatSlug
	const isCollectionObject = isCollectionObjectSlug(props.collection_slug)
	const isRemoteCollection = props.isRemote || isCollectionObject
	const showMobileLayout = isMobile

	// Activity sidebar collapsed state. Sales Activity is open by default for all
	// collections on desktop (parity with fungibles); NFT collections keep the
	// collapse toggle so it can still be hidden.
	const [isActivityCollapsed, setIsActivityCollapsed] = useState(false)

	// NAT-only: desktop sidebar view switcher between Sales Activity and Owners
	// Which sidebar tab is open — held here so "view owners" can select it.
	// A link to a conversation (`?post=<id>`, e.g. from a notification) lands
	// on the Social tab, which is where that thread opens.
	const [sidebarView, setSidebarView] = useState(() =>
		typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('post')
			? 'social'
			: 'activity'
	)
	// NAT-only: the holders view joins the sidebar's single tab row.
	const natTabs = useMemo<SidebarTab[]>(
		() =>
			tapTicker
				? [
						{
							id: 'owners',
							label: 'Alpha',
							render: (height: string) => <NatOwners height={height} hideHeader ticker={tapTicker} />
						}
				  ]
				: [],
		[tapTicker]
	)

	return (
		<div
			ref={rootRef}
			id="nat-market"
			data-collection-shell={isMobile ? undefined : 'token'}
			className={
				isMobile
					? `flex min-h-0 flex-col ${isTapDmtNat ? 'w-full max-w-[100vw]' : ''}`
					: `flex h-[calc(100dvh-var(--ow-header-h,56px)-32px)] max-h-[calc(100dvh-var(--ow-header-h,56px)-32px)] min-h-[420px] flex-col ${isTapDmtNat ? 'w-full max-w-[100vw]' : ''}`
			}
		>
			<div ref={headerRef}>
				{!showMobileLayout && (
					<CollectionStats
						data={props.collectionStats}
						collection={props.metaData}
						slug={props.collection_slug}
						isRemote={isRemoteCollection}
						hideStats={false}
						chartRange={chartRange}
						onChartRangeChange={handleChartRangeChange}
						headerActions={props.headerActions}
					/>
				)}
			</div>
			{isTapDmtNatSlug && isMobile && <NatOffersPanel />}
			<div
				ref={containerRef}
				className={isMobile ? 'max-w-[100vw]' : 'flex min-h-0 flex-1 flex-col overflow-hidden max-w-[100vw]'}
			>
				{isMobile ? (
					<>
						<Tabs value={activeTab} onValueChange={switchTab} className="flex flex-col" ref={ref}>
							{!showMobileLayout && (
								<TabsList className="bg-background border-b border-border grid grid-cols-3 h-12 rounded-none shrink-0 p-0">
									<TabsTrigger
										value="feed"
										className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:ring-0 data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-foreground"
									>
										{props.leftPanel ? (
											<Info size={12} className="mr-1" />
										) : (
											<ShoppingCart size={12} className="mr-1" />
										)}
										<span className="text-[8px]">{props.leftPanel ? 'About' : 'Market'}</span>
									</TabsTrigger>
									<TabsTrigger
										value="chart"
										className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:ring-0 data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-foreground"
									>
										<BarChart2 size={12} className="mr-1" />
										<span className="text-[8px]">{isTapDmtNat ? 'Insights' : 'Chart'}</span>
									</TabsTrigger>
									<TabsTrigger
										value="activity"
										className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:ring-0 data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-foreground"
									>
										<History size={12} className="mr-1" />
										<span className="text-[8px]">Activity</span>
									</TabsTrigger>
								</TabsList>
							)}
							<TabsContent
								value="feed"
								className={`w-full ${showMobileLayout ? 'mt-0 pb-24' : ''}`}
							>
								{showMobileLayout && (
									<div className="flex items-center gap-2 border-b p-3">
										<button
											type="button"
											onClick={() => switchTab('chart')}
											className="shrink-0 rounded p-1 text-foreground/80 hover:bg-white/5"
											aria-label="Back"
										>
											<ChevronLeft size={20} />
										</button>
										<CollectionIcon
											className="size-7 shrink-0 overflow-hidden rounded-full"
											collection={props.metaData}
										/>
										<h2 className="flex min-w-0 flex-1 items-center gap-1 truncate text-base font-semibold text-foreground">
											<span className="truncate">{props.metaData?.name}</span>
											<VerifiedBadge slug={props.collection_slug} />
										</h2>
										{/* Icon-only quick access to the chart and sales activity
									    views (the mobile layout has no top tab bar). 16px gap,
									    square rounded hover target, theme-aware in light/dark. */}
										<div className="flex shrink-0 items-center gap-4">
											{props.headerActions && (
												<div className="flex shrink-0 items-center">{props.headerActions}</div>
											)}
											<button
												type="button"
												onClick={() => switchTab('chart')}
												aria-label="View chart"
												className="flex size-8 items-center justify-center rounded-md text-foreground/70 transition-colors hover:bg-foreground/10 hover:text-foreground"
											>
												<BarChart2 size={18} />
											</button>
											<button
												type="button"
												onClick={() => switchTab('activity')}
												aria-label="View activity"
												className="flex size-8 items-center justify-center rounded-md text-foreground/70 transition-colors hover:bg-foreground/10 hover:text-foreground"
											>
												<History size={18} />
											</button>
										</div>
									</div>
								)}
								<ErrorBoundary>
									{props.leftPanel || (
										<Feed
											height={containerHeight}
											title={props.title}
											items={props.inscriptions}
											slug={props.collection_slug}
											supply={props.metaData?.total_supply}
											listed={props.collectionStats?.listed}
											meta={props.metaData}
											isMobile={props.isMobile}
											attributes={attributes}
											onSelectBulk={handleSelectBulk}
											bulkSelected={{}}
											isFungible={isFungibleCollection as any}
											isRune={isRune}
											onSelectionCountChange={setSelectionCount}
										/>
									)}
								</ErrorBoundary>
							</TabsContent>
							<TabsContent
								value="chart"
								className={`w-full ${showMobileLayout ? 'mt-0 pb-24' : ''}`}
							>
								<ErrorBoundary>
									{/* Every token, NAT included, lands on the same quick-view insights
									    body on mobile: price, simple chart, market stats. */}
									{showMobileLayout ? (
										<CollectionStats
											data={props.collectionStats}
											collection={props.metaData}
											slug={props.collection_slug}
											isRemote={isRemoteCollection}
											hideStats={false}
											chartRange={chartRange}
											onChartRangeChange={handleChartRangeChange}
											mode="insights"
											chartMaxWidth={10000}
											onViewActivity={() => switchTab('activity')}
											headerActions={props.headerActions}
										/>
									) : (
										<CollectionChart
											slug={props.collection_slug}
											collection={props.metaData}
											width={width}
											minimal
											showPrice
											className={MOBILE_COLLECTION_CHART_CLASS}
											onRangeChange={handleChartRangeChange}
										/>
									)}
								</ErrorBoundary>
							</TabsContent>
							<TabsContent
								value="activity"
								className={`w-full ${showMobileLayout ? 'mt-0 pb-24' : ''}`}
							>
								{showMobileLayout && (
									<div className="flex items-center gap-2 border-b p-3">
										<button
											type="button"
											onClick={() => switchTab('chart')}
											className="shrink-0 rounded p-1 text-foreground/80 hover:bg-white/5"
											aria-label="Back"
										>
											<ChevronLeft size={20} />
										</button>
										<CollectionIcon
											className="size-7 shrink-0 overflow-hidden rounded-full"
											collection={props.metaData}
										/>
										<h2 className="flex min-w-0 items-center gap-1 truncate text-base font-semibold text-foreground">
											<span className="truncate">{props.metaData?.name}</span>
											<VerifiedBadge slug={props.collection_slug} />
										</h2>
										{props.headerActions && (
											<div className="ml-auto flex shrink-0 items-center">
												{props.headerActions}
											</div>
										)}
									</div>
								)}
								<ErrorBoundary>
																	<ActivitySocialPanel
										slug={props.collection_slug}
										name={props.metaData?.name}
										height={containerHeight}
										hideHeader={showMobileLayout || hasTapAlpha}
										extraTabs={natTabs}
										view={sidebarView}
										onViewChange={setSidebarView}
									/>
								</ErrorBoundary>
							</TabsContent>
						</Tabs>
						{showMobileLayout && activeTab !== 'feed' && (
							<div className="fixed bottom-8 left-0 right-0 z-40 border-t bg-background px-4 py-3">
								<button
									type="button"
									onClick={() => switchTab('feed')}
									className="h-11 w-full rounded-full bg-[#6CE9A6] text-sm font-semibold text-black hover:bg-[#7FECB2] active:bg-[#5DD999]"
								>
									Trade now
								</button>
							</div>
						)}
					</>
				) : (
					<div
						className={`grid border-t overflow-hidden ${isTapDmtNat ? 'min-h-0' : 'h-full'}`}
						style={{
							height: isTapDmtNat ? containerHeight : undefined,
							gridTemplateRows: isTapDmtNat ? 'minmax(0, 1fr)' : undefined,
							gridTemplateColumns: `${COLLECTION_RAIL_WIDTH} minmax(0, 1fr) ${!isFungibleCollection && isActivityCollapsed ? '48px' : COLLECTION_RAIL_WIDTH}`
						}}
					>
						<div
							className={`w-full min-w-0 overflow-y-auto ${
								isMobile && activeTab !== 'feed' ? 'hidden' : ''
							}`}
						>
							<ErrorBoundary>
								{props.leftPanel || (
									<Feed
										height={containerHeight}
										title={props.title}
										items={props.inscriptions}
										slug={props.collection_slug}
										supply={props.metaData?.total_supply}
										listed={props.collectionStats?.listed}
										meta={props.metaData}
										isMobile={props.isMobile}
										attributes={attributes}
										onSelectBulk={handleSelectBulk}
										bulkSelected={{}}
										isFungible={isFungibleCollection as any}
										isRune={isRune}
										onSelectionCountChange={setSelectionCount}
									/>
								)}
							</ErrorBoundary>
						</div>
						<div
							className={`w-full min-w-0 ${
								isMobile ? '' : 'h-full min-h-[320px]'
							} ${
								isFungibleCollection && !isTapDmtNat ? 'overflow-hidden' : 'overflow-y-auto'
							} ${isMobile && activeTab !== 'chart' ? 'hidden' : ''}`}
							ref={ref}
							id="collection-feed"
						>
							<ErrorBoundary>
								{isFungibleCollection ? (
									<CollectionChart
										slug={props.collection_slug}
										collection={props.metaData}
										width={width}
										className={DESKTOP_COLLECTION_CHART_CLASS}
										minimal
										showPrice
										onRangeChange={handleChartRangeChange}
									/>
								) : (
									<>
										{isRemoteCollection && (
											<RemoteCollectionFeed
												title={props.title}
												items={props.inscriptions}
												slug={props.collection_slug}
												supply={props.metaData?.total_supply}
												listed={props.collectionStats?.listed}
												meta={props.metaData}
												isMobile={props.isMobile}
												attributes={attributes}
												onSelectBulk={handleSelectBulk}
												bulkSelected={{}}
												disablePreview={isActivityCollapsed}
											/>
										)}
										{!isRemoteCollection && (
											<CollectionFeed
												title={props.title}
												items={props.inscriptions}
												slug={props.collection_slug}
												supply={props.metaData?.total_supply}
												listed={props.collectionStats?.listed}
												meta={props.metaData}
												isMobile={props.isMobile}
												attributes={attributes}
												onSelectBulk={handleSelectBulk}
												bulkSelected={{}}
												disablePreview={isActivityCollapsed}
											/>
										)}
									</>
								)}
							</ErrorBoundary>
						</div>
						{/* Activity Sidebar - collapsible for NFT collections */}
						<div className="h-full border-l overflow-hidden bg-chrome-muted dark:bg-transparent">
							{!isFungibleCollection && isActivityCollapsed ? (
								<button
									onClick={() => setIsActivityCollapsed(false)}
									className="size-full flex items-center justify-center hover:bg-white/5 transition-colors"
									title="Show Sales Activity"
								>
									<ChevronLeft className="size-5 text-muted-foreground" />
								</button>
							) : (
								<div className="h-full flex flex-col">
									{isTapDmtNatSlug && <div className="max-h-[50vh] overflow-y-auto shrink-0"><NatOffersPanel /></div>}
									<div className="flex-1 overflow-hidden">
										<ErrorBoundary>
																					<ActivitySocialPanel
												slug={props.collection_slug}
												name={props.metaData?.name}
												height={containerHeight}
												hideHeader={!isFungibleCollection || hasTapAlpha}
												onCollapse={!isFungibleCollection ? () => setIsActivityCollapsed(true) : undefined}
												extraTabs={natTabs}
												view={sidebarView}
												onViewChange={setSidebarView}
											/>
										</ErrorBoundary>
									</div>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	)
}

export default CollectionComponent
